import React, { useEffect, useState } from "react";
import axios from "axios";
import { MapPin, Gift, Users, Phone, ArrowRight, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const img = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

function BranchMap({ b }) {
  if (!b?.lat) return (
    <div className="h-44 rounded-2xl bg-zinc-100 flex flex-col items-center justify-center text-xs text-zinc-500 gap-1" data-testid="circuit-map-fallback">
      <MapPin className="h-5 w-5" /> Mappa non disponibile
      {b?.address && <a className="underline" target="_blank" rel="noreferrer" href={`https://www.openstreetmap.org/search?query=${encodeURIComponent(b.address)}`}>Cerca su OpenStreetMap</a>}
    </div>
  );
  const d = 0.004;
  const bbox = `${b.lon - d},${b.lat - d},${b.lon + d},${b.lat + d}`;
  return <iframe title={`Mappa ${b.name}`} className="h-44 w-full rounded-2xl border border-zinc-200" loading="lazy" data-testid="circuit-map"
    src={`https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${b.lat},${b.lon}`} />;
}

// Mini-scheda pubblica di un circuito (vetrina): sedi sulla mappa + premi in evidenza + CTA
export default function CircuitPreview({ slug, open, onClose, link, onEnter }) {
  const [d, setD] = useState(null);
  const [sel, setSel] = useState(0);
  useEffect(() => {
    if (!open || !slug) return;
    setD(null); setSel(0);
    axios.get(`${API}/platform/public/circuits/${slug}`).then((r) => setD(r.data)).catch(() => setD({ error: true }));
  }, [open, slug]);
  const b = d?.branches?.[sel];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto bg-white text-zinc-900 border-zinc-200" data-testid="circuit-preview">
        {!d ? <p className="text-sm text-zinc-500 py-8 text-center">Caricamento…</p> : d.error ? <p className="text-sm text-red-600 py-8 text-center">Circuito non disponibile.</p> : (<>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3" style={{ color: d.brand }} data-testid="circuit-preview-title">
              {d.logo_url ? <img src={img(d.logo_url)} alt="" className="h-9 object-contain" /> : null}{d.name}
            </DialogTitle>
            {d.tagline && <p className="text-sm text-zinc-600">{d.tagline}</p>}
          </DialogHeader>
          <div className="grid grid-cols-3 gap-2 text-center" data-testid="circuit-preview-stats">
            {[[Users, d.stats.customers, "clienti"], [MapPin, d.stats.branches, d.stats.branches === 1 ? "sede" : "sedi"], [Gift, d.stats.rewards, "premi"]].map(([Icon, v, l]) => (
              <div key={l} className="rounded-2xl py-2" style={{ background: `${d.brand}12` }}><Icon className="h-4 w-4 mx-auto" style={{ color: d.brand }} /><div className="font-bold font-mono">{Number(v).toLocaleString("it-IT")}</div><div className="text-[10px] uppercase tracking-wider text-zinc-500">{l}</div></div>
            ))}
          </div>
          {d.branches.length > 0 && (
            <section className="space-y-2" data-testid="circuit-preview-branches">
              <h3 className="text-sm font-semibold flex items-center gap-1.5"><MapPin className="h-4 w-4" style={{ color: d.brand }} /> Punti vendita</h3>
              {d.branches.length > 1 && <div className="flex gap-1.5 flex-wrap">{d.branches.map((x, i) => <button key={x.id} type="button" onClick={() => setSel(i)} className="px-3 py-1 rounded-full text-xs border transition-colors" style={i === sel ? { background: d.brand, color: "#fff", borderColor: d.brand } : { borderColor: "#e4e4e7" }} data-testid={`circuit-branch-${x.id}`}>{x.name}</button>)}</div>}
              <BranchMap b={b} />
              <div className="text-xs text-zinc-600 flex flex-wrap gap-x-4 gap-y-1"><span className="font-semibold text-zinc-800">{b.name}</span>{b.address && <span>{b.address}</span>}{b.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {b.phone}</span>}</div>
            </section>
          )}
          {d.rewards.length > 0 && (
            <section className="space-y-2" data-testid="circuit-preview-rewards">
              <h3 className="text-sm font-semibold flex items-center gap-1.5"><Gift className="h-4 w-4" style={{ color: d.brand }} /> Premi in evidenza</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {d.rewards.map((r) => (
                  <div key={r.id} className="rounded-2xl border border-zinc-200 p-2.5 flex flex-col gap-1" data-testid={`circuit-reward-${r.id}`}>
                    {r.image_url ? <img src={img(r.image_url)} alt="" className="h-20 w-full object-cover rounded-xl" /> : <div className="h-20 rounded-xl flex items-center justify-center" style={{ background: `${d.brand}12` }}><Gift className="h-6 w-6" style={{ color: d.brand }} /></div>}
                    <div className="text-xs font-semibold leading-tight line-clamp-2">{r.name}</div>
                    {r.points_cost != null && <div className="text-[11px] font-mono font-bold" style={{ color: d.brand }}>{r.points_cost} punti</div>}
                  </div>
                ))}
              </div>
            </section>
          )}
          <a href={link} onClick={onEnter} className="flex items-center justify-center gap-2 rounded-full py-3 text-white font-semibold" style={{ background: d.brand }} data-testid="circuit-preview-enter">Entra nel circuito <ArrowRight className="h-4 w-4" /></a>
          {d.gift_card && <a href={link.replace("/home?", "/home/gift-card?")} onClick={onEnter} className="flex items-center justify-center gap-2 rounded-full py-2.5 font-semibold border-2" style={{ color: d.brand, borderColor: d.brand }} data-testid="circuit-preview-gift">
            <Gift className="h-4 w-4" /> Regala una {d.gift_card_title || "Gift Card"}</a>}
          {d.domain && <div className="text-[11px] text-zinc-500 text-center font-mono inline-flex items-center justify-center gap-1 w-full"><ExternalLink className="h-3 w-3" /> {d.domain}</div>}
        </>)}
      </DialogContent>
    </Dialog>
  );
}
